/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/browser';
import { PLAYER_TYPES } from '../Constant';

let session: any = null; // Store MediaTailor session
let context: any = null; // Store Datazoom Context

let digitalAdControlsEnabled = false;

export const setDigitalAdControlsEnabled = (enabled: boolean): void => {
  digitalAdControlsEnabled = enabled;
};

export const destroyMediaTailorSession = (): void => {
  if (!session) {
    return;
  }
  session?.destroy();
  context?.detachMediaTailorSession?.();
  context?.destroy();
  if (session?._trackedListeners) {
    session._trackedListeners.forEach(({ type, handler }) => {
      session.removeEventListener(type, handler);
    });
  }
  session._trackedListeners = null;
  session?.removeAdObserver?.();
  session = null;
  context = null;
};

/**
 * Returns adapter options for Datazoom based on player type.
 */
const getPlayerAdapterOptions = (playerType: string, player): object | '' => {
  if (playerType === PLAYER_TYPES.SHAKA) {
    return { adapterOptions: { shakaRoot: player } };
  }
  return '';
};
const attachAdEventListeners = (
  mediatailor: any,
  setIsAdPlaying?: (isAdPlaying: boolean) => void,
) => {
  const adStartHandler = (event: any) => {
    const currentAdData = event?.detail?.adObject?.adData;
    const playCount = parseInt(currentAdData?.playCount, 10);
    setIsAdPlaying?.(playCount < 2);
  };

  const adEndHandler = () => {
    setIsAdPlaying?.(false);
  };

  session.addEventListener(mediatailor.SessionUiEvent.AD_START, adStartHandler);
  session.addEventListener(mediatailor.SessionUiEvent.AD_END, adEndHandler);

  session._trackedListeners = [
    { type: mediatailor.SessionUiEvent.AD_START, handler: adStartHandler },
    { type: mediatailor.SessionUiEvent.AD_END, handler: adEndHandler },
  ];
};

type InitializeMediaTailorSessionParams = {
  datazoom: any;
  playbackAsset: {
    adTrackingUri?: string;
  };
  player: any;
  setIsAdPlaying?: (isAdPlaying: boolean) => void;
  playerType: string;
  configurationId: string;
};
/**
 * Initializes MediaTailor session and attaches it to the player.
 */
export const initializeMediaTailorSession = async ({
  datazoom,
  playbackAsset,
  player,
  setIsAdPlaying,
  playerType,
  configurationId,
}: InitializeMediaTailorSessionParams): Promise<void> => {
  const adtrackingUrl = playbackAsset?.adTrackingUri;
  try {
    datazoom.init({ configuration_id: configurationId });

    const { mediatailor } = datazoom;
    /* to enable datazoom and mediatailor logs
      datazoom.setLogLevel('verbose');
      mediatailor.setLogLevel(mediatailor.LogLevel.DEBUG) */

    const sessionConfig = { trackingUrl: adtrackingUrl };
    session = await mediatailor.createSession(sessionConfig);
    context = datazoom.createContext(player, getPlayerAdapterOptions(playerType, player));
    context?.attachMediaTailorSession(session);
    if (digitalAdControlsEnabled) {
      attachAdEventListeners(mediatailor, setIsAdPlaying);
    }
  } catch (error) {
    Sentry.captureEvent({
      message: 'MediaTailor session creation failed',
      level: 'error',
      extra: {
        category: 'Digital Ad',
        message: `adTrackingUri = ${playbackAsset?.adTrackingUri}`,
        error,
      },
    });
  }
};

export default initializeMediaTailorSession;
